import type { ServerEvent } from '@aw/contracts';
import { offlineError, toApiError } from './errors';
import type {
  AllowanceRequest,
  AnswerInput,
  AuditEvent,
  AuditFilter,
  BreakGlassReset,
  BreakGlassResetStarted,
  Client,
  ClientFile,
  ClientImportResult,
  ClientListFilter,
  ClientNote,
  ClientRow,
  ConversationView,
  DeliverableDetail,
  DocumentView,
  EmailDraft,
  EntraConfig,
  FactView,
  FirmSettings,
  FirmUsageView,
  HomeSummary,
  IntegrationKey,
  IntegrationStatus,
  ManagedAgentsConfig,
  MessageView,
  NeedsListView,
  Person,
  QuestionView,
  ReturnType,
  RunView,
  Session,
  TaxYearView,
  UsageView,
  UserView,
  YearSummary,
} from './types';
import {
  isApiError,
  type StreamStatus,
  type Unsubscribe,
  type UploadContext,
  type WorkspaceApi,
} from './WorkspaceApi';

const BASE = '/api/v1';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * The real api. Session cookie (HttpOnly, set by the api) plus the CSRF token from /me on every
 * state change. Nothing here holds a secret.
 */
/** The server pings every 25 s (SSE_HEARTBEAT_MS, contracts events.ts `StreamPing`). */
const PING_MS = 25_000;
const PING_SLACK_MS = 10_000;

export class HttpApi implements WorkspaceApi {
  readonly mode = 'http' as const;
  private csrf: string | null = null;

  constructor(private readonly fetchImpl: typeof fetch = (...a) => globalThis.fetch(...a)) {}

  private async request<T>(method: Method, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    let payload: BodyInit | undefined;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    if (method !== 'GET' && this.csrf) headers['X-CSRF-Token'] = this.csrf;
    let res: Response;
    try {
      res = await this.fetchImpl(`${BASE}${path}`, {
        method,
        headers,
        credentials: 'same-origin',
        ...(payload === undefined ? {} : { body: payload }),
      });
    } catch {
      throw offlineError();
    }
    if (!res.ok) {
      let parsed: unknown = null;
      try {
        parsed = await res.json();
      } catch {
        // not json (a proxy page, a gateway error): the status decides the code
      }
      const err = toApiError(res.status, parsed, res.statusText || 'The request failed.');
      if (err.code === 'unauthorized') this.csrf = null;
      throw err;
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  private get<T>(path: string) {
    return this.request<T>('GET', path);
  }
  private post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, body ?? {});
  }
  private put<T>(path: string, body: unknown) {
    return this.request<T>('PUT', path, body);
  }
  private patch<T>(path: string, body: unknown) {
    return this.request<T>('PATCH', path, body);
  }

  private remember(session: Session): Session {
    this.csrf = session.csrfToken;
    return session;
  }

  setupStatus() {
    return this.get<{ needsSetup: boolean }>('/setup/status');
  }
  setup(input: { email: string; displayName: string; password: string }) {
    return this.post<{ totpSecretUri: string }>('/setup', input);
  }
  async signInLocal(input: { email: string; password: string; totp: string }) {
    return this.remember(await this.post<Session>('/auth/local', input));
  }
  entraStartUrl(returnTo = '/') {
    const safe = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
    return `${BASE}/auth/entra/start?returnTo=${encodeURIComponent(safe)}`;
  }
  async signInDev(email: string) {
    return this.remember(await this.post<Session>('/auth/dev', { email }));
  }
  async signOut() {
    await this.post<void>('/auth/signout');
    this.csrf = null;
  }
  async me() {
    return this.remember(await this.get<Session>('/me'));
  }

  /**
   * GET /events. One `data:` JSON ServerEvent per message; EventSource resumes with Last-Event-ID
   * after a drop. `session_ended` means signed out: the stream closes and the app goes to sign-in.
   */
  /** the last stored event this browser saw, so a reopened stream resumes after it */
  private lastEventId: string | null = null;

  /**
   * Live updates over SSE. The browser retries a dropped connection by itself; when it gives up
   * (the stream closed, as on a deploy's 502), this asks /me: only a 401 means the session is
   * over, anything else reopens the stream after a pause, resuming from the last event seen.
   */
  subscribe(
    listener: (event: ServerEvent) => void,
    onStatus?: (s: StreamStatus) => void,
    onReset?: () => void,
  ): Unsubscribe {
    if (typeof EventSource === 'undefined') return () => {};
    let source: EventSource | null = null;
    let stopped = false;
    let retry: Parameters<typeof clearTimeout>[0];
    let watchdog: Parameters<typeof clearTimeout>[0];
    let attempt = 0;
    const end = () => {
      stopped = true;
      this.csrf = null;
      onStatus?.('ended');
    };
    const reopenLater = () => {
      if (stopped) return;
      onStatus?.('reconnecting');
      attempt += 1;
      retry = setTimeout(open, Math.min(3000 * 2 ** (attempt - 1), 30_000));
    };
    const open = () => {
      if (stopped) return;
      if (attempt === 0) onStatus?.('connecting');
      const since = this.lastEventId ? `?since=${encodeURIComponent(this.lastEventId)}` : '';
      const s = new EventSource(`${BASE}/events${since}`, { withCredentials: true });
      source = s;
      // Once the server has pinged, silence means a dead stream (a socket lost while the machine
      // slept): two missed pings close it and reopen through the usual backoff. A server that
      // never pings is never judged by them.
      let pinged = false;
      const alive = () => {
        clearTimeout(watchdog);
        if (!pinged || stopped) return;
        watchdog = setTimeout(
          () => {
            if (stopped || source !== s) return;
            s.close();
            reopenLater();
          },
          2 * PING_MS + PING_SLACK_MS,
        );
      };
      s.onopen = () => {
        attempt = 0;
        onStatus?.('live');
        alive();
      };
      s.onerror = () => {
        if (stopped) return;
        clearTimeout(watchdog);
        if (s.readyState !== EventSource.CLOSED) {
          onStatus?.('reconnecting');
          return;
        }
        s.close();
        this.me().then(reopenLater, (e: unknown) =>
          isApiError(e, 'unauthorized') ? end() : reopenLater(),
        );
      };
      s.onmessage = (e: MessageEvent<string>) => {
        alive();
        if (e.lastEventId) this.lastEventId = e.lastEventId;
        try {
          listener(JSON.parse(e.data) as ServerEvent);
        } catch {
          // ignore a malformed event rather than break the stream
        }
      };
      s.addEventListener('session_ended', () => {
        clearTimeout(watchdog);
        s.close();
        end();
      });
      s.addEventListener('ping', () => {
        pinged = true;
        alive();
      });
      // The gap can't be replayed: reload every view, and stop resuming from the old id.
      s.addEventListener('reset', () => {
        this.lastEventId = null;
        alive();
        onReset?.();
      });
    };
    open();
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearTimeout(watchdog);
      source?.close();
    };
  }

  home() {
    return this.get<HomeSummary>('/home');
  }
  people() {
    return this.get<Person[]>('/people');
  }

  listClients(filter: ClientListFilter = 'all', search?: { q: string; limit?: number }) {
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('filter', filter);
    if (search?.q) params.set('q', search.q);
    if (search?.q && search.limit) params.set('limit', String(search.limit));
    const query = params.toString();
    return this.get<ClientRow[]>(`/clients${query ? `?${query}` : ''}`);
  }
  createClient(input: { name: string; kind: Client['kind']; returnTypes: ReturnType[] }) {
    return this.post<Client>('/clients', input);
  }
  clientFile(clientId: string) {
    return this.get<ClientFile>(`/clients/${clientId}/file`);
  }
  listYears(clientId: string) {
    return this.get<TaxYearView[]>(`/clients/${clientId}/years`);
  }
  acceptFact(factId: string) {
    return this.post<FactView>(`/facts/${factId}/accept`);
  }
  dismissNote(noteId: string) {
    return this.post<ClientNote>(`/notes/${noteId}/dismiss`);
  }
  acceptNote(noteId: string) {
    return this.post<ClientNote>(`/notes/${noteId}/accept`);
  }
  rejectFact(factId: string) {
    return this.post<FactView>(`/facts/${factId}/reject`);
  }
  async addNote(
    clientId: string,
    input: { body: string; kind: 'note' | 'remember_next_year'; taxYearId: string | null },
  ) {
    await this.post(`/clients/${clientId}/notes`, input);
  }

  year(taxYearId: string) {
    return this.get<YearSummary>(`/years/${taxYearId}`);
  }
  markFiled(taxYearId: string) {
    return this.post<TaxYearView>(`/years/${taxYearId}/mark-filed`, { confirm: true });
  }
  unlockYear(taxYearId: string, reason: string) {
    return this.post<TaxYearView>(`/years/${taxYearId}/unlock`, { confirm: true, reason });
  }
  needs(taxYearId: string) {
    return this.get<NeedsListView>(`/years/${taxYearId}/needs`);
  }
  waiveNeed(taxYearId: string, itemId: string, reason: string) {
    return this.post<NeedsListView>(`/years/${taxYearId}/needs/${itemId}/waive`, { reason });
  }
  needsEmail(taxYearId: string) {
    return this.get<EmailDraft>(`/years/${taxYearId}/needs/email`);
  }
  async emailCopied(messageId: string) {
    await this.post<void>(`/messages/${messageId}/email-copied`);
  }

  /** Multipart upload over XHR so the drop overlay can show progress; fetch has no upload events. */
  uploadDocuments(files: File[], context: UploadContext): Promise<DocumentView[]> {
    const form = new FormData();
    if (context.conversationId) form.append('conversationId', context.conversationId);
    if (files.length > 0 && files.every((f) => f.type.startsWith('image/')))
      form.append('source', 'photo');
    for (const f of files) form.append('files', f, f.name);
    if (typeof XMLHttpRequest === 'undefined' || !context.onProgress) {
      return this.request<DocumentView[]>('POST', '/documents', form);
    }
    const onProgress = context.onProgress;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE}/documents`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      if (this.csrf) xhr.setRequestHeader('X-CSRF-Token', this.csrf);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onerror = () => reject(offlineError());
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText || 'null');
        } catch {
          // keep null
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          resolve(body as DocumentView[]);
        } else reject(toApiError(xhr.status, body, xhr.statusText || 'The upload failed.'));
      };
      xhr.send(form);
    });
  }
  document(documentId: string) {
    return this.get<DocumentView>(`/documents/${documentId}`);
  }
  retryRead(documentId: string) {
    return this.post<DocumentView>(`/documents/${documentId}/retry-read`);
  }
  questions() {
    return this.get<QuestionView[]>('/questions');
  }
  question(questionId: string) {
    return this.get<QuestionView>(`/questions/${questionId}`);
  }
  answerQuestion(questionId: string, input: AnswerInput) {
    return this.post<QuestionView>(`/questions/${questionId}/answer`, input);
  }
  undoAnswer(questionId: string) {
    return this.post<QuestionView>(`/questions/${questionId}/undo`);
  }

  startDeliverable(taxYearId: string, kind: string) {
    return this.post<RunView>(`/years/${taxYearId}/deliverables`, { kind });
  }
  deliverable(deliverableId: string) {
    return this.get<DeliverableDetail>(`/deliverables/${deliverableId}`);
  }
  async keepFigure(figureId: string) {
    await this.post(`/figures/${figureId}/keep`);
  }
  async correctFigure(figureId: string, input: { reason: string; newAmount: number }) {
    await this.post(`/figures/${figureId}/correct`, input);
  }
  async markReviewed(deliverableId: string) {
    await this.post(`/deliverables/${deliverableId}/mark-reviewed`);
  }

  conversations() {
    return this.get<ConversationView[]>('/conversations');
  }
  conversation(conversationId: string) {
    return this.get<ConversationView>(`/conversations/${conversationId}`);
  }
  createConversation(input: {
    scope: 'firm' | 'client' | 'tax_year';
    clientId: string | null;
    taxYearId: string | null;
    visibility: 'team' | 'private';
    title: string;
  }) {
    return this.post<ConversationView>('/conversations', input);
  }
  messages(conversationId: string) {
    return this.get<MessageView[]>(`/conversations/${conversationId}/messages`);
  }
  sendMessage(conversationId: string, input: { text: string; attachDocumentIds: string[] }) {
    return this.post<{ message: MessageView; run: RunView | null }>(
      `/conversations/${conversationId}/messages`,
      input,
    );
  }
  run(runId: string) {
    return this.get<RunView>(`/runs/${runId}`);
  }
  stopRun(runId: string) {
    return this.post<RunView>(`/runs/${runId}/stop`);
  }

  usageMe() {
    return this.get<UsageView>('/usage/me');
  }
  usageFirm() {
    return this.get<FirmUsageView>('/usage/firm');
  }
  async requestAllowance(input: { amountCents: number; reason: string | null }) {
    await this.post('/allowance-requests', input);
  }
  allowanceRequests() {
    return this.get<AllowanceRequest[]>('/allowance-requests?status=all');
  }
  async decideAllowance(requestId: string, approve: boolean) {
    await this.post(`/allowance-requests/${requestId}/decide`, {
      decision: approve ? 'approve' : 'decline',
    });
  }
  firmSettings() {
    return this.get<FirmSettings>('/settings/firm');
  }
  saveFirmSettings(settings: FirmSettings) {
    return this.put<FirmSettings>('/settings/firm', settings);
  }
  users() {
    return this.get<UserView[]>('/users');
  }
  updateUser(
    userId: string,
    patch: { monthlyAllowanceCents?: number; role?: UserView['role']; status?: UserView['status'] },
  ) {
    return this.patch<UserView>(`/users/${userId}`, patch);
  }
  unlinkIdentity(userId: string, reason: string) {
    return this.post<UserView>(`/users/${userId}/unlink-identity`, { reason });
  }
  integrations() {
    return this.get<IntegrationStatus[]>('/settings/integrations');
  }
  setSecret(key: IntegrationKey, value: string) {
    return this.put<IntegrationStatus[]>('/settings/integrations/secret', { key, value });
  }
  validateIntegration(key: IntegrationKey) {
    return this.post<IntegrationStatus>(`/settings/integrations/${key}/validate`);
  }
  entra() {
    return this.get<EntraConfig>('/settings/entra');
  }
  saveEntra(config: EntraConfig) {
    return this.put<EntraConfig>('/settings/entra', config);
  }
  agentsConfig() {
    return this.get<ManagedAgentsConfig>('/settings/agents');
  }
  syncAgents() {
    return this.post<ManagedAgentsConfig>('/settings/agents/sync');
  }
  importClients(file: File) {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.post<ClientImportResult>('/clients/import', form);
  }
  breakGlassResets() {
    return this.get<BreakGlassReset[]>('/break-glass-resets');
  }
  startBreakGlassReset(reason: string) {
    return this.post<BreakGlassResetStarted>('/break-glass-resets', { reason });
  }
  confirmBreakGlassReset(resetId: string) {
    return this.post<BreakGlassReset>(`/break-glass-resets/${resetId}/confirm`);
  }
  cancelBreakGlassReset(resetId: string) {
    return this.post<BreakGlassReset>(`/break-glass-resets/${resetId}/cancel`);
  }
  audit(filter: AuditFilter) {
    return this.get<AuditEvent[]>(`/audit${filter === 'everything' ? '' : `?filter=${filter}`}`);
  }
}
